import { formatPrice } from '@/components/page-specific/menu-manager/menu-list/helpers'
import PreviewActionBar from '@/components/PreviewActionBar'
import PreviewStatusChip, { type PreviewStatus } from '@/components/PreviewStatusChip'
import QuantitySelectV2 from '@/components/QuantitySelectV2'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { MenuItemOptionType } from '@/generated/graphql'
import { numberFormat } from '@/helpers/numbers'
import { main } from '@caterdesk/ui--theme'
import { DietaryRow } from '@fusion/dietary/row'
import { Close } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import NoImageAvailable from '@public/images/no-image-available.png'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { optionsAreValid } from '../ccMenuSpecificHelpers'
import { CartMappingOptionInput } from '../MenuBody/cartMapping'
import MenuItemDetailTabs from './MenuItemDetailTabs'

export const canteenMenuItemDetailItemDataFragment = graphql(`
  fragment CanteenMenuItemDetail_ItemDataFragmentV2 on MenuItem {
    ...CanteenMenuItemDetail_ItemData_TabsFragment
    _id
    name
    image
    description
    dietaries
    allergens
    isCombo
    subAllergens {
      cereals
      nuts
    }
    variations {
      _id
      priceCustomerFacing
      priceExTax
    }
    options {
      _id
      categoryType
      position
      minQty
      maxQty
      qtyPerPerson
      optionType
      name
      items {
        position
        _id
        price
        priceExTax
        item {
          _id
          activeFrom
          name
          dietaries
          allergens
          subAllergens {
            cereals
            nuts
          }
        }
      }
    }
  }
`)

const canteenMenuItemDetailVendorDataFragment = graphql(`
  fragment CanteenMenuItemDetail_VendorDataFragmentV2 on Vendor {
    id
    companyName
    sourcing
    logo
  }
`)

const canteenMenuOrderedItemDetailOptionsDataFragment = graphql(`
  fragment CanteenMenuItemDetail_OrderedOptionsDataFragmentV2 on CCOrderItemOptions {
    items {
      id
      name
      position
      price
      category
      qty
      dietaries
      allergens
      subAllergens {
        cereals
        nuts
      }
      liveMenuItem {
        _id
      }
    }
  }
`)

type BaseProps = {
  selectedVariationId: string
  itemData: FragmentType<typeof canteenMenuItemDetailItemDataFragment>
  liveVariationQuantityData: {
    orderLimit: number | null
    quantityRemaining: number | null
  }
  vendorData: FragmentType<typeof canteenMenuItemDetailVendorDataFragment>
  hidePrices: boolean
  onClose: () => void
  fullScreen: boolean
}

type NewItemOrderProps = BaseProps & {
  isOrderedItem: false
  mode?: never
  disableAddToCart: boolean
  maxQuantity: number | null
  onAddToCart: (quantity: number, options: Array<CartMappingOptionInput>) => void
}

type OrderedItemProps = BaseProps & {
  isOrderedItem: true
  mode?: never
  orderedItemId: number
  orderedOptionsData: FragmentType<typeof canteenMenuOrderedItemDetailOptionsDataFragment>
  onCancelItem: (ccOrderItemId: number) => void
  canCancel?: boolean
}

type PreviewItemProps = BaseProps & {
  isOrderedItem: false
  mode: 'preview'
  status: PreviewStatus
  /** Vendor currency symbol; the preview prices like the menu list cards. */
  currencySymbol: string
  /** Shown after the price, e.g. " ex 10% GST". */
  priceSuffix?: string
  onDuplicateClick?: () => void
  onEditClick: () => void
  onDeleteClick?: () => void
}

type Props = NewItemOrderProps | OrderedItemProps | PreviewItemProps

const MenuItemDetailV2: React.FC<Props> = (props) => {
  const {
    isOrderedItem,
    selectedVariationId,
    itemData: itemDataProp,
    liveVariationQuantityData,
    vendorData: vendorDataProp,
    hidePrices,
    onClose,
    fullScreen,
  } = props

  const orderedOptionsData = props.isOrderedItem
    ? useFragment(canteenMenuOrderedItemDetailOptionsDataFragment, props.orderedOptionsData)
    : null

  const theme = useTheme()

  const itemData = useFragment(canteenMenuItemDetailItemDataFragment, itemDataProp)
  const {
    name,
    image,
    variations,
    options,
    allergens,
    dietaries,
    subAllergens,
    isCombo,
    description,
  } = itemData
  const { orderLimit, quantityRemaining } = liveVariationQuantityData

  const vendorData = useFragment(canteenMenuItemDetailVendorDataFragment, vendorDataProp)
  const { companyName, sourcing, logo } = vendorData

  const { t } = useTranslation()
  const [quantity, setQuantity] = useState(1)
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, Record<string, { qty: number; price: number; name: string }>>
  >({})
  const [awaitingDeleteConfirmation, setAwaitingDeleteConfirmation] = useState(false)

  const setOption = (
    optionData: {
      categoryId: string
      optionId: string
      price: number
      name: string
    },
    optionQuantity: number,
  ) => {
    setSelectedOptions((prev) => {
      const newOptionState = {
        ...prev,
        [optionData.categoryId]: {
          ...prev[optionData.categoryId],
          [optionData.optionId]: {
            price: optionData.price,
            qty: optionQuantity,
            name: optionData.name,
          },
        },
      }
      if (optionQuantity === 0) {
        delete newOptionState[optionData.categoryId][optionData.optionId]
      }
      return newOptionState
    })
  }

  const handleAddToCart = () => {
    if (props.isOrderedItem || props.mode === 'preview') return
    const optionsToAdd = Object.entries(selectedOptions).flatMap((optionCategory) => {
      const [categoryId, optionItems] = optionCategory
      return Object.entries(optionItems).map((optionItem) => {
        const [itemId, optionItemData] = optionItem
        const returnOption: CartMappingOptionInput = {
          item: {
            id: itemId,
            name: optionItemData.name,
            price: optionItemData.price,
          },
          category: { id: categoryId },
        }
        return returnOption
      })
    })
    props.onAddToCart(quantity, optionsToAdd)
  }

  const handleCancelItemIntention = () => {
    setAwaitingDeleteConfirmation(true)
  }

  const handleCancelItemConfirmation = (confirmCancel: boolean) => {
    if (!isOrderedItem) return
    if (confirmCancel) {
      props.onCancelItem(props.orderedItemId)
    } else {
      setAwaitingDeleteConfirmation(false)
    }
  }

  const ccVariation = variations?.find((variation) => variation._id === selectedVariationId)
  if (!ccVariation) throw new Error('Something wrong with this item')

  const validOptions = options ? optionsAreValid(options, selectedOptions) : true
  const selectedOptionsTotal = Object.values(selectedOptions).reduce((total, optionCategory) => {
    return Object.values(optionCategory).reduce((categoryTotal, option) => {
      return categoryTotal + option.price * option.qty
    }, total)
  }, 0)

  // The menu manager preview is for vendors and admins, who price items ex tax.
  const isPreview = props.mode === 'preview'
  const displayedItemPrice = isPreview
    ? (ccVariation.priceExTax ?? 0)
    : ccVariation.priceCustomerFacing

  const itemTotal = (ccVariation.priceCustomerFacing || 0) + selectedOptionsTotal
  const itemQuantityTotal = itemTotal * quantity
  return (
    <Card sx={{ padding: 2, height: '100%' }}>
      <Grid container height="100%" direction={'row'} spacing={1}>
        {fullScreen ? null : (
          <Grid size={5} height="100%">
            <CardMedia
              sx={{
                height: '100%',
                position: 'relative',
                borderRadius: 4,
                alignContent: 'end',
              }}
              title={name || ''}
              image={image || NoImageAvailable.src}>
              <Box>
                {typeof quantityRemaining === 'number' &&
                quantityRemaining > 0 &&
                quantityRemaining <= 10 ? (
                  <Box
                    sx={{
                      padding: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      borderRadius: '0 0 16px 16px',
                    }}>
                    <Typography
                      color={main.colors.text_contrast}
                      variant="caption"
                      fontWeight={500}>
                      {quantityRemaining > 0 ? `Only ${quantityRemaining} Left` : 'Sold Out!'}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </CardMedia>
          </Grid>
        )}

        <Grid size={{ xs: 12, sm: 7, md: 7 }} height="100%">
          <Stack height="inherit" justifyContent="space-between">
            <Stack sx={{ overflowY: 'auto' }}>
              <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
                paddingLeft={2}
                alignItems="center">
                <Stack
                  display="inline-flex"
                  width="100%"
                  justifyContent="space-between"
                  direction="row"
                  alignItems="center"
                  sx={{ overflowWrap: 'anywhere' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h4" fontWeight={'normal'}>
                      {name}
                    </Typography>
                    {props.mode === 'preview' && <PreviewStatusChip status={props.status} />}
                  </Stack>
                  {!hidePrices ? (
                    <Typography variant="body1" fontSize={24} sx={{ fontWeight: '600' }}>
                      {props.mode === 'preview'
                        ? formatPrice(displayedItemPrice, props.currencySymbol)
                        : `${numberFormat(displayedItemPrice / 100)} ${t('common:credits')}`}
                      {props.mode === 'preview' && props.priceSuffix ? (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {props.priceSuffix}
                        </Typography>
                      ) : null}
                    </Typography>
                  ) : null}
                </Stack>
                <IconButton data-cy="close-cc-item-modal" onClick={onClose}>
                  <Close />
                </IconButton>
              </Stack>
              {orderLimit && (
                <Stack paddingLeft={2} marginBottom={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('cc:dialog.orderLimitText', { orderLimit })}
                  </Typography>
                </Stack>
              )}
              <CardContent sx={{ overflowY: 'auto' }}>
                <Stack direction="column" spacing={2}>
                  <Stack spacing={1} alignItems="center" direction="row">
                    {logo ? (
                      <Avatar sx={{ width: 20, height: 20 }} alt={companyName} src={logo} />
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      {t('cc:freshlyPreparedBy', { companyName })}
                    </Typography>
                  </Stack>
                  <DietaryRow
                    dietaries={dietaries || []}
                    allergens={allergens || []}
                    subAllergens={subAllergens}
                    isComboItem={Boolean(isCombo)}
                  />
                  <MenuItemDetailTabs
                    description={description}
                    itemData={itemData}
                    companyName={companyName}
                    sourcing={sourcing}
                  />
                  <Stack direction="column" spacing={2}>
                    {options && options.length > 0
                      ? options.map((option) => {
                          if (!option) return null
                          const hasMinQuantity = typeof option?.minQty === 'number' && option.minQty
                          const hasMaxQuantity =
                            typeof option?.maxQty === 'number' && option.maxQty > 0

                          const minQtyLabel = t('cc:dialog.optionsMinQty', {
                            minQty: option.minQty,
                          })
                          const maxQtyLabel = t('cc:dialog.optionsMaxQty', {
                            maxQty: option.maxQty,
                          })

                          const quantityLabel =
                            hasMinQuantity && hasMaxQuantity
                              ? `${minQtyLabel} ${maxQtyLabel}`
                              : hasMinQuantity
                                ? minQtyLabel
                                : hasMaxQuantity
                                  ? maxQtyLabel
                                  : ''
                          return (
                            <Card sx={{ paddingY: 1 }} key={option._id}>
                              <Stack
                                direction="row"
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                sx={{
                                  borderBottom: '1px solid',
                                  borderBottomColor: 'divider',
                                  px: 1.5,
                                  paddingBottom: 1,
                                }}>
                                <Typography variant="subtitle3">{option.name}</Typography>
                                {quantityLabel && (
                                  <Chip
                                    label={quantityLabel}
                                    sx={{
                                      backgroundColor: alpha(theme.palette.primary.light, 0.15),
                                      color: theme.palette.text.secondary,
                                    }}
                                  />
                                )}
                              </Stack>
                              {option.items && option.items.length > 0
                                ? option.items.map((optionItem) => {
                                    const optionItemId = optionItem?._id
                                    const coreOptionItemId = optionItem?.item?._id
                                    if (!optionItemId) return null
                                    const optionQuantity =
                                      selectedOptions[option._id]?.[optionItemId]?.qty || 0
                                    const numberOfSelectedOptionsInCategory = Object.values(
                                      selectedOptions[option._id] || {},
                                    ).reduce((total, value) => total + value.qty, 0)
                                    return (
                                      <Stack
                                        paddingRight={1}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        direction="row"
                                        key={optionItem._id}>
                                        <Stack
                                          alignItems="center"
                                          spacing={1}
                                          justifyContent={'start'}
                                          direction="row"
                                          sx={{ ...(isOrderedItem && { color: 'text.disabled' }) }}>
                                          {match(option.optionType)
                                            .with(MenuItemOptionType.Ticks, () => {
                                              const checked =
                                                isOrderedItem && orderedOptionsData
                                                  ? (orderedOptionsData.items.find(
                                                      (item) =>
                                                        String(item.liveMenuItem?._id) ===
                                                        coreOptionItemId,
                                                    )?.qty ?? 0) > 0
                                                  : optionQuantity > 0
                                              return (
                                                <FormControlLabel
                                                  label={
                                                    <Typography variant="body2" color="inherit">
                                                      {optionItem?.item?.name}
                                                    </Typography>
                                                  }
                                                  control={
                                                    <Checkbox
                                                      size="small"
                                                      sx={{ padding: '7px 9px' }}
                                                      disabled={
                                                        isOrderedItem ||
                                                        Boolean(
                                                          !optionQuantity &&
                                                          option.maxQty &&
                                                          numberOfSelectedOptionsInCategory ===
                                                            option.maxQty,
                                                        )
                                                      }
                                                      onChange={(e) =>
                                                        setOption(
                                                          {
                                                            categoryId: option._id,
                                                            optionId: optionItemId,
                                                            price: optionItem.price || 0,
                                                            name: optionItem.item?.name || '',
                                                          },
                                                          e.target.checked ? 1 : 0,
                                                        )
                                                      }
                                                      checked={checked}
                                                    />
                                                  }
                                                  checked={checked}
                                                />
                                              )
                                            })
                                            .otherwise(() => (
                                              <></>
                                            ))}
                                          <DietaryRow
                                            maxToShow={2}
                                            dietaries={optionItem?.item?.dietaries || []}
                                            allergens={optionItem?.item?.allergens || []}
                                            subAllergens={optionItem?.item?.subAllergens}
                                          />
                                        </Stack>
                                        {!hidePrices ? (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              ...(isOrderedItem && { color: 'text.disabled' }),
                                            }}>
                                            {(() => {
                                              const optionPrice =
                                                (isPreview
                                                  ? optionItem?.priceExTax
                                                  : optionItem?.price) || 0
                                              return optionPrice === 0
                                                ? t('common:free')
                                                : `+${numberFormat(optionPrice / 100)}`
                                            })()}
                                          </Typography>
                                        ) : null}
                                      </Stack>
                                    )
                                  })
                                : null}
                            </Card>
                          )
                        })
                      : null}
                  </Stack>
                </Stack>
              </CardContent>
            </Stack>
            {props.mode === 'preview' ? (
              <CardActions sx={{ paddingBottom: 0, paddingLeft: 2 }}>
                <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end">
                  <PreviewActionBar
                    onDuplicate={props.onDuplicateClick}
                    onEdit={props.onEditClick}
                    onDelete={props.onDeleteClick}
                  />
                </Stack>
              </CardActions>
            ) : isOrderedItem ? (
              <CardActions sx={{ paddingBottom: 0 }}>
                {!awaitingDeleteConfirmation ? (
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    disabled={isOrderedItem && props.canCancel === false}
                    onClick={handleCancelItemIntention}
                    data-cy="canteen-ordered-item-cancel">
                    {t('cc:dialog.cancelItemButton')}
                  </Button>
                ) : (
                  <Stack direction="column" spacing={1} width="100%" alignItems="center">
                    <Typography textAlign="center">
                      {t('cc:cancelWarningText', { name })}
                    </Typography>
                    <Stack direction="row" spacing={1} width="100%">
                      <Button
                        fullWidth
                        variant="contained"
                        color="error"
                        onClick={() => handleCancelItemConfirmation(true)}>
                        {t('cc:dialog.confirmCancelItemButton')}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleCancelItemConfirmation(false)}>
                        {t('cc:dialog.keepItemButton')}
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </CardActions>
            ) : (
              <CardActions sx={{ paddingBottom: 0, paddingLeft: 2 }}>
                <QuantitySelectV2
                  min={1}
                  max={props.maxQuantity}
                  disabled={props.disableAddToCart}
                  quantity={quantity}
                  onChange={(value) => setQuantity(value)}
                />
                <Stack width="100%">
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleAddToCart()}
                    disabled={!validOptions || props.disableAddToCart}
                    data-cy="canteen-menu-item-add-to-cart">
                    {validOptions ? (
                      <>
                        {t('cc:dialog.addToCart', {
                          count: quantity,
                        })}{' '}
                        {!hidePrices ? <>({numberFormat(itemQuantityTotal / 100)})</> : null}
                      </>
                    ) : (
                      <>{t('cc:dialog.chooseOptions')}</>
                    )}
                  </Button>
                </Stack>
              </CardActions>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  )
}

export default MenuItemDetailV2
